import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

interface SessionGuardProps {
  /**
   * Inactivity timeout in milliseconds.
   * If undefined or 0, no inactivity timer is set — session stays alive as long
   * as the browser keeps the Supabase refresh token (ideal for users filling forms).
   * Always redirects to login on SIGNED_OUT regardless of this setting.
   */
  inactivityMs?: number;
}

/**
 * SessionGuard — mounts inside authenticated areas (portal + admin).
 *
 * - With inactivityMs (admin/staff): signs out after N ms of no activity.
 * - Without inactivityMs (portal users): no forced timeout — Supabase's
 *   refresh token keeps the session alive across browser restarts.
 * - Always redirects to login on SIGNED_OUT (expired token, manual logout, etc.)
 */
const SessionGuard = ({ inactivityMs }: SessionGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const redirectToLogin = () => {
    const redirect = encodeURIComponent(location.pathname + location.search);
    navigate(`/portal/login?redirect=${redirect}`, { replace: true });
  };

  const signOutAndRedirect = async () => {
    try { await supabase.auth.signOut(); } catch {}
    redirectToLogin();
  };

  const resetTimer = () => {
    if (!inactivityMs) return; // No timeout for portal users
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(signOutAndRedirect, inactivityMs);
  };

  useEffect(() => {
    // Start inactivity timer only if configured (admin/staff)
    if (inactivityMs) {
      resetTimer();
      ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    }

    // Always listen for session expiry events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        redirectToLogin();
      } else if (event === "TOKEN_REFRESHED" && inactivityMs) {
        resetTimer(); // Active session — reset inactivity timer
      }
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (inactivityMs) {
        ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, resetTimer));
      }
      subscription.unsubscribe();
    };
  }, [location.pathname]);

  return null;
};

export default SessionGuard;
